import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const TargetCoursePanel = ({
  courseStructure = [],
  selectedItems = [],
  onSelectItems,
  onDrop,
  connections = [],
  onRemoveConnection,
  onPreview
}) => {
  const [expandedCourses, setExpandedCourses] = useState([1]); // Default first course expanded
  const [expandedModules, setExpandedModules] = useState([101]); // Default first module expanded
  const [dragOverItem, setDragOverItem] = useState(null);

  const toggleCourse = (courseId) => {
    setExpandedCourses(prev => 
      prev?.includes(courseId)
        ? prev?.filter(id => id !== courseId)
        : [...prev, courseId]
    );
  };

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => 
      prev?.includes(moduleId)
        ? prev?.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleSelectItem = (id, checked) => {
    if (checked) {
      onSelectItems?.(prev => [...prev, id]);
    } else {
      onSelectItems?.(prev => prev?.filter(itemId => itemId !== id));
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, item) => {
    e?.preventDefault();
    setDragOverItem(item?.id);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setDragOverItem(null);
  };

  const handleDrop = (e, item) => {
    e?.preventDefault();
    setDragOverItem(null);
    onDrop?.(item);
  };

  const getConnectionForLesson = (lessonId) => {
    return connections?.find(conn => conn?.targetId === lessonId);
  };

  const getMediaTypeIcon = (mediaType) => {
    switch (mediaType) {
      case 'video': return 'Video';
      case 'image': return 'Image';
      case 'document': return 'FileText';
      case 'audio': return 'Volume2';
      default: return 'File';
    }
  };

  const LessonItem = ({ lesson, moduleTitle }) => {
    const isSelected = selectedItems?.includes(lesson?.id);
    const isDragOver = dragOverItem === lesson?.id;
    const connection = getConnectionForLesson(lesson?.id);
    const hasMedia = lesson?.currentMedia || connection;
    const hasConflict = connection?.status === 'conflict';

    return (
      <div
        className={`ml-8 p-4 rounded-lg border transition-all duration-200 ${
          isDragOver 
            ? 'border-primary bg-primary/10 shadow-tier-2' 
            : hasConflict
            ? 'border-error bg-error/5'
            : hasMedia
            ? 'border-success bg-success/5'
            : lesson?.mediaRequired
            ? 'border-warning bg-warning/5' :'border-border bg-card'
        } ${
          lesson?.mediaRequired && !hasMedia ? 'hover:border-warning/60' : 'hover:border-border'
        }`}
        onDragOver={handleDragOver}
        onDragEnter={(e) => handleDragEnter(e, lesson)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, lesson)}
        data-testid={`target-lesson-${lesson?.id}`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1">
            <Checkbox
              checked={isSelected}
              onChange={(e) => handleSelectItem(lesson?.id, e?.target?.checked)}
              className="mt-1"
            />

            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="FileText" size={16} color="var(--color-muted-foreground)" />
                <h4 className="font-medium text-foreground text-sm" title={lesson?.title}>
                  {lesson?.title}
                </h4>
                
                {/* Media type indicator */}
                {lesson?.mediaType && (
                  <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                    <Icon name={getMediaTypeIcon(lesson?.mediaType)} size={12} />
                    <span className="capitalize">{lesson?.mediaType}</span>
                  </div>
                )}
              </div>

              {/* Media Status */}
              <div className="flex items-center space-x-2 mb-2">
                {lesson?.mediaRequired && (
                  <span className="text-xs font-medium text-warning">
                    Mídia Obrigatória
                  </span>
                )}
                
                {hasConflict && (
                  <div className="flex items-center space-x-1 text-xs text-error">
                    <Icon name="AlertCircle" size={12} />
                    <span>Conflito Detectado</span>
                  </div>
                )}
                
                {hasMedia && !hasConflict && (
                  <div className="flex items-center space-x-1 text-xs text-success">
                    <Icon name="CheckCircle" size={12} />
                    <span>Mídia Mapeada</span>
                  </div>
                )}
                
                {lesson?.mediaRequired && !hasMedia && (
                  <div className="flex items-center space-x-1 text-xs text-warning">
                    <Icon name="AlertTriangle" size={12} />
                    <span>Aguardando Mídia</span>
                  </div>
                )}
              </div>

              {/* Current Media Info */}
              {hasMedia && (
                <div className="bg-muted/50 rounded-lg p-3 mt-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={getMediaTypeIcon(lesson?.currentMedia?.type || 'video')} 
                        size={14} 
                        color="var(--color-muted-foreground)" 
                      />
                      <span className="text-sm text-foreground font-medium">
                        Mídia Atual
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="xs"
                        iconName="Eye"
                        onClick={() => onPreview?.(lesson?.id)}
                        className="h-6 px-2"
                      >
                        Preview
                      </Button>
                      
                      {connection && (
                        <Button
                          variant="ghost"
                          size="xs"
                          iconName="X"
                          onClick={() => onRemoveConnection?.(connection?.connectionId)}
                          className="h-6 px-2 text-error hover:text-error"
                        >
                          Remover
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Drop Zone */}
              {lesson?.mediaRequired && !hasMedia && (
                <div className="border-2 border-dashed border-warning/40 rounded-lg p-4 mt-2 text-center">
                  <Icon name="Upload" size={20} color="var(--color-warning)" className="mx-auto mb-2" />
                  <p className="text-sm text-warning font-medium">
                    Arraste mídia aqui
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Aceita: {lesson?.mediaType || 'qualquer tipo'}
                  </p>
                </div>
              )}

              {/* Module Context */}
              <div className="text-xs text-muted-foreground mt-2">
                Módulo: {moduleTitle}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ModuleItem = ({ module, courseTitle }) => {
    const isExpanded = expandedModules?.includes(module?.id);
    const lessonsWithMedia = module?.lessons?.filter(lesson => 
      lesson?.currentMedia || getConnectionForLesson(lesson?.id)
    )?.length || 0;
    const totalLessons = module?.lessons?.length || 0;
    const requiredLessons = module?.lessons?.filter(lesson => lesson?.mediaRequired)?.length || 0;

    return (
      <div className="ml-6">
        <div
          className="flex items-center justify-between p-3 rounded-lg hover:bg-accent/50 cursor-pointer transition-colors"
          onClick={() => toggleModule(module?.id)}
        >
          <div className="flex items-center space-x-3">
            <Icon 
              name="ChevronRight" 
              size={16} 
              className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            />
            <Icon name="Layers" size={18} color="var(--color-secondary)" />
            <div>
              <h3 className="font-medium text-foreground">{module?.title}</h3>
              <div className="flex items-center space-x-4 text-xs text-muted-foreground mt-1">
                <span>{totalLessons} lições</span>
                <span>{requiredLessons} requer mídia</span>
                <span className={lessonsWithMedia === requiredLessons ? 'text-success' : 'text-warning'}>
                  {lessonsWithMedia}/{requiredLessons} mapeadas
                </span>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center space-x-2">
            <div className="w-12 bg-muted rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  lessonsWithMedia === requiredLessons ? 'bg-success' : 'bg-warning'
                }`}
                style={{ 
                  width: requiredLessons > 0 ? `${(lessonsWithMedia / requiredLessons) * 100}%` : '0%'
                }}
              />
            </div>
            <span className="text-xs text-muted-foreground">
              {requiredLessons > 0 ? Math.round((lessonsWithMedia / requiredLessons) * 100) : 0}%
            </span>
          </div>
        </div>

        {/* Lessons */}
        {isExpanded && (
          <div className="mt-2 space-y-2">
            {module?.lessons?.map((lesson) => (
              <LessonItem 
                key={lesson?.id} 
                lesson={lesson} 
                moduleTitle={module?.title}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const CourseItem = ({ course }) => {
    const isExpanded = expandedCourses?.includes(course?.id);
    const totalModules = course?.modules?.length || 0;
    const totalLessons = course?.modules?.reduce((acc, module) => acc + (module?.lessons?.length || 0), 0) || 0;
    const mappedLessons = course?.modules?.reduce((acc, module) => 
      acc + (module?.lessons?.filter(lesson => 
        lesson?.currentMedia || getConnectionForLesson(lesson?.id)
      )?.length || 0), 0) || 0;

    return (
      <div className="border border-border rounded-lg overflow-hidden">
        <div
          className="flex items-center justify-between p-4 bg-card hover:bg-accent/30 cursor-pointer transition-colors border-b border-border"
          onClick={() => toggleCourse(course?.id)}
        >
          <div className="flex items-center space-x-3">
            <Icon 
              name="ChevronRight" 
              size={18} 
              className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            />
            <Icon name="Book" size={20} color="var(--color-primary)" />
            <div>
              <h2 className="font-semibold text-foreground text-lg">{course?.title}</h2>
              <div className="flex items-center space-x-4 text-sm text-muted-foreground mt-1">
                <span>{totalModules} módulos</span>
                <span>{totalLessons} lições</span>
                <span className={`${mappedLessons > 0 ? 'text-success' : 'text-muted-foreground'}`}>
                  {mappedLessons} com mídia
                </span>
              </div>
            </div>
          </div>

          {/* Course Progress */}
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <div className="text-sm font-medium text-foreground">
                {totalLessons > 0 ? Math.round((mappedLessons / totalLessons) * 100) : 0}%
              </div>
              <div className="text-xs text-muted-foreground">Completo</div>
            </div>
            <div className="w-16 bg-muted rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: totalLessons > 0 ? `${(mappedLessons / totalLessons) * 100}%` : '0%'
                }}
              />
            </div>
          </div>
        </div>

        {/* Modules */}
        {isExpanded && (
          <div className="p-4 space-y-4">
            {course?.modules?.map((module) => (
              <ModuleItem 
                key={module?.id} 
                module={module} 
                courseTitle={course?.title}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-tier-1">
      {/* Panel Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-secondary/10 rounded-lg">
            <Icon name="Target" size={20} color="var(--color-secondary)" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Estrutura do Curso</h2>
            <p className="text-sm text-muted-foreground font-caption">
              Solte mídia nas lições para criar mapeamentos
            </p>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <div className="text-lg font-bold text-foreground">
              {courseStructure?.reduce((acc, course) => acc + (course?.modules?.length || 0), 0)}
            </div>
            <div className="text-xs text-muted-foreground">Módulos</div>
          </div>
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <div className="text-lg font-bold text-foreground">
              {courseStructure?.reduce((acc, course) => 
                acc + course?.modules?.reduce((moduleAcc, module) => 
                  moduleAcc + (module?.lessons?.length || 0), 0), 0)}
            </div>
            <div className="text-xs text-muted-foreground">Lições</div>
          </div>
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <div className="text-lg font-bold text-success">
              {courseStructure?.reduce((acc, course) => 
                acc + course?.modules?.reduce((moduleAcc, module) => 
                  moduleAcc + module?.lessons?.filter(lesson => 
                    lesson?.currentMedia || getConnectionForLesson(lesson?.id)
                  )?.length, 0), 0)}
            </div>
            <div className="text-xs text-muted-foreground">Mapeadas</div>
          </div>
        </div>
      </div>

      {/* Course Structure */}
      <div className="p-6">
        {courseStructure?.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-24 h-24 mx-auto mb-4 bg-muted/50 rounded-full flex items-center justify-center">
              <Icon name="BookOpen" size={32} color="var(--color-muted-foreground)" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Nenhum curso encontrado
            </h3>
            <p className="text-muted-foreground font-caption">
              Configure a estrutura do curso para começar o mapeamento
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {courseStructure?.map((course) => (
              <CourseItem key={course?.id} course={course} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TargetCoursePanel;